import React, { FC, useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

// Components
import BaseContentContainer from '../../components/base-content-container/base-content-container';
import ButtonsBar from '../../components/buttons-bar/buttons-bar';
import Form from '../../components/form/form';
import StudentSelector from '../../components/student-selector/student-selector';
import SubmitButton from '../../components/submit-button/submit-button';
import Loading from '../../components/loading/loading';
import ErrorComponent from '../../components/error/error';
import PasswordRequirements from '../../components/password-requirements/password-requirements';

// Form Data
import { formDataEmployeeCreate } from '../../form-data/form-data-employee-create';

// Helpers
import {
    getEmployeeData,
    handleFormOnChange,
    checkIfAllFormHasFilled,
    handleStudentChange, deleteEmployee,
} from '../../helpers/employee-helper';
import { getErrorField, getErrorMessage } from '../../helpers/api-error-helper';

// Models
import ApiError from '../../models/api-error';
import {
    BaseContentContainerActionButtonPosition,
    BaseContentContainerBiggerSide,
    BaseContentContainerFormat,
} from '../../models/base-content-container';

// Providers
import { GeneralContext } from '../../providers/general-provider';

// Services
import ManagementService from '../../services/management-service';
import ClassroomService from "../../services/classroom-service";

const EmployeeEdit: FC = () => {
    const navigate = useNavigate();
    const { setModalContent, setToastContent } = useContext(GeneralContext);
    const managementService = new ManagementService();
    const classroomService = new ClassroomService();
    const [error, setError] = useState(false);
    const [data, setData] = useState<EmployeeFormData>(null);
    const { employeeId } = useParams<{ employeeId: string }>();
    

    const [employee, setEmployee] = useState<Employee>(null);
    const [validations, setValidations] = useState({});
    const [allowSubmit, setAllowSubmit] = useState(true);


    useEffect(() => {
        managementService
            .getEmployee(parseInt(employeeId))
            .then((result) => {
                const employeeData = getEmployeeData(result);
                setData(employeeData);
                setEmployee(result);
            })
            .catch((err) =>  {
                console.error(err);
                setError(true);
            });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect(() => {
        checkIfAllFormHasFilled(data, validations, allowSubmit, setAllowSubmit, true);
    }, [validations, allowSubmit, data]); // eslint-disable-line react-hooks/exhaustive-deps

    const [formElements, setFormElements] = useState<FormParams>(null);
    const [classroomList, setClassroomList] = useState<
        ClassroomStudents[] | null
    >(null);
    useEffect(() => {
        classroomService
            .listClassroomStudents()
            .then((classroomsResponse) => {
                if (!employee) {
                    return;
                }

                const selectedClassrooms = employee.classroomIds;

                handleStudentChange(
                    selectedClassrooms,
                    [],
                    data,
                    setData
                );
                setClassroomList(classroomsResponse);
                setFormElements(formDataEmployeeCreate(employee));
            })
            .catch((err) => {
                console.error(err);
                return setError(true);
            });
    }, [employee]); // eslint-disable-line react-hooks/exhaustive-deps

    const updateEmployeePromise = (submitData: CreateEmployeeFormData) => {
        return managementService
            .updateEmployee(submitData, parseInt(employeeId))
            .then(() => {
                setToastContent({
                    isOpen: true,
                    text: 'Funcionário salvo!',
                    type: 'success',
                });
                navigate('/gerenciar');
            })
            .catch((e: ApiError) => {
                const text = getErrorMessage(e, 'Houve um erro ao salvar o funcionário.');
                const field = getErrorField(e);

                if (field) {
                    setValidations((current) => ({ ...current, [field]: e.message }));
                }

                setToastContent({ isOpen: true, text, type: 'error' });
            });
    };

    const handleSubmit = () => {
        const submitData = Object.assign({}, data);

        const mapping: CreateEmployeeFormData = {
            name:  submitData.formData.name,
            email: submitData.formData.email,
            password: submitData.formData.password,
            role: submitData.formData.role,
            classroomIds: submitData.classData
        }

        updateEmployeePromise(mapping);
    };

    if (error) return <ErrorComponent type='contentFull' />;

    if (!formElements || !classroomList) return <Loading type='contentFull' />;

    return (
        <BaseContentContainer
            biggerSide={BaseContentContainerBiggerSide.right}
            format={BaseContentContainerFormat.doubleWithActionButtons}
            actionButtonPosition={BaseContentContainerActionButtonPosition.bottom}
        >
            <Form
                formData={formElements}
                validations={validations}
                handleOnChange={(evt) =>
                    handleFormOnChange(
                        evt,
                        validations,
                        data,
                        setValidations,
                        setData,
                        true,
                    )
                }
                fieldHints={{
                    password: (
                        <PasswordRequirements password={data.formData.password} />
                    ),
                }}
            />
            {classroomList && (
                <StudentSelector
                    classrooms={classroomList}
                    handleStudentChange={(
                        selectedClassrooms: number[],
                        selectedStudents: number[]
                    ) =>
                        handleStudentChange(
                            selectedClassrooms,
                            selectedStudents,
                            data,
                            setData
                        )
                    }
                    initialClassrooms={data.classData}
                    initialStudents={[]}
                    hideStudentSelection={true}
                />
            )}
            <ButtonsBar pattern='firstSeparate' verticalAlign='bottom'>
                <Link to='/gerenciar'>
                    <SubmitButton value='cancelar' variant='terciary' />
                </Link>
                <SubmitButton
                    value='deletar'
                    variant='terciary'
                    onClick={() =>
                        deleteEmployee(
                            parseInt(employeeId),
                            setModalContent,
                            setToastContent,
                            navigate
                        )
                    }
                />
                <SubmitButton
                    value='salvar'
                    variant='primary'
                    onClick={handleSubmit}
                    disabled={!allowSubmit}
                />
            </ButtonsBar>
        </BaseContentContainer>
    );
};

export default EmployeeEdit;
